import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { OpportunitesTabs } from "@/components/TalentSectionTabs";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, MessageCircle, ArrowRight, Inbox } from "lucide-react";

export default function Missions() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user && !["talent", "professional"].includes(user.role)) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/missions")
        .then(({ data }) => setMissions(Array.isArray(data) ? data : []))
        .catch(() => setMissions([]))
        .finally(() => setReady(true));
    }
  }, [user]);

  if (!user) return null;

  return (
    <AppShell>
      <div data-testid="missions-page" className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        {user.role === "talent" ? (
          <OpportunitesTabs />
        ) : (
          <>
            <p className="caption-label text-[#A26F3E]">Missions actives</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1">Mes collaborations</h1>
            <p className="text-sm text-[#8B8680] mt-3 max-w-xl">
              Une fois une candidature acceptée, retrouvez ici la fiche mission, le chat privé et les avis.
            </p>
          </>
        )}

        {ready && missions.length === 0 && (
          <div className="bg-white rounded-3xl border border-[#EADDC9] p-12 text-center mt-10" data-testid="missions-empty">
            <div className="w-14 h-14 rounded-full bg-[#FAF3EA] flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-6 h-6 text-[#C68E59]" />
            </div>
            <p className="font-serif text-xl text-[#1A1A1A]">Aucune mission active pour l'instant</p>
            <p className="text-sm text-[#8B8680] mt-2">Vos collaborations acceptées apparaîtront ici.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {missions.map((m) => {
            const other = user.role === "talent" ? m.pro : m.talent;
            const otherCompany = user.role === "talent" ? m.pro_profile?.company_name : null;
            const stage = m.status;
            const stageColor = stage === "completed" ? "bg-[#E7F5EC] text-[#2E7D32] border-[#BFE4C5]"
              : stage === "reminded" ? "bg-[#1A1A1A] text-[#D4A574]"
              : "bg-[#FAF3EA] text-[#A26F3E] border-[#E6C29A]";
            return (
              <Link
                to={`/missions/${m.id}`}
                key={m.id}
                className="bg-white rounded-3xl border border-[#EADDC9] p-6 hover:border-[#C68E59] transition flex flex-col gap-3"
                data-testid={`mission-card-${m.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="caption-label text-[#A26F3E]">Mission</p>
                    <h3 className="font-serif text-2xl text-[#1A1A1A] mt-0.5 leading-tight truncate">{m.title}</h3>
                  </div>
                  <span className={`text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border font-semibold flex-shrink-0 ${stageColor}`}>
                    {stage === "completed" ? "Terminée" : stage === "reminded" ? "Imminente" : "Planifiée"}
                  </span>
                </div>
                <div className="text-sm text-[#6E4B2A] space-y-1.5">
                  <p className="font-semibold text-[#1A1A1A] truncate">{otherCompany || other?.name}</p>
                  {m.mission_date && (
                    <p className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />
                      {new Date(m.mission_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      {m.mission_time && <span> · {m.mission_time}</span>}
                    </p>
                  )}
                  {m.location && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{m.location}</p>}
                </div>
                <div className="pt-3 mt-auto border-t border-[#FAF3EA] flex items-center justify-between text-xs">
                  <span className="text-[#8C7D6B] flex items-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5" />
                    {m.last_message ? m.last_message.body.slice(0, 40) : "Aucun message"}
                    {m.unread_count > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#C68E59] text-white text-[10px] font-semibold">
                        {m.unread_count}
                      </span>
                    )}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#1A1A1A]" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
